/* eslint-disable @typescript-eslint/no-explicit-any */
import type { FunctionComponent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { berlinShortDate } from "./format.js";
import { Lightbox, resolveColor } from "./ListView.js";
import { loadLeaflet } from "./leaflet-loader.js";
import type { EmbedConfig, PublicAction, PublicOrg } from "./types.js";

const BERLIN_CENTER: [number, number] = [52.52, 13.405];
const BERLIN_ZOOM = 11;
// Below this zoom every marker is a coloured dot; at/above it, an org logo
// (if present) is shown so a zoomed-in view is recognisable (M6.9 / M2.14).
const LOGO_ZOOM = 13;

// Presentational: the org + full upcoming set are fetched upstream
// (useEmbedActions) and shared with the list, so the map always plots every
// event — never the list's initial slice (M2.6 — past actions are not mapped).
export const MapView: FunctionComponent<{
  config: EmbedConfig;
  org: PublicOrg | null;
  actions: PublicAction[];
}> = ({ config, org, actions }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const clusterRef = useRef<any>(null);
  const LRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Only ever tracks Leaflet's own load lifecycle now that the data arrives
  // via props.
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Only geocoded actions carry a marker, so only those can be stepped through.
  const markerIds = actions.filter((a) => a.lat != null && a.lng != null).map((a) => a.id);

  // The map's `zoomend` handler is registered once but must see the latest
  // org (colour/logo), so it reads through a ref instead of a stale closure.
  const orgRef = useRef<PublicOrg | null>(org);
  orgRef.current = org;

  // Load Leaflet (lazily, off-bundle) and initialise the map once.
  useEffect(() => {
    let cancelled = false;
    const host = containerRef.current;
    if (!host) return;
    const cssTarget = (host.getRootNode() as ShadowRoot | Document) ?? document;

    loadLeaflet(config.vendorBase ?? `${config.apiBase}/vendor/leaflet/`, cssTarget)
      .then((L: any) => {
        if (cancelled || mapRef.current || !containerRef.current) return;
        LRef.current = L;
        const map = L.map(containerRef.current, {
          center: BERLIN_CENTER,
          zoom: BERLIN_ZOOM,
          scrollWheelZoom: true,
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);
        const cluster = L.markerClusterGroup({
          showCoverageOnHover: false,
          spiderfyOnMaxZoom: true,
          maxClusterRadius: 50,
        });
        map.addLayer(cluster);
        // Swap dot ⇄ logo markers when the zoom crosses the threshold.
        map.on("zoomend", () => applyIcons(map.getZoom()));
        mapRef.current = map;
        clusterRef.current = cluster;
        setStatus((s) => (s === "error" ? s : "ready"));
        requestAnimationFrame(() => map.invalidateSize());
      })
      .catch(() => { if (!cancelled) setStatus("error"); });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        clusterRef.current = null;
      }
    };
  }, [config.apiBase, config.vendorBase]);

  // Build the dot/logo icons for the current org. Reads through refs so the
  // once-registered zoomend handler always sees current org data.
  function makeIcons() {
    const L = LRef.current;
    const current = orgRef.current;
    const color = current ? resolveColor(current.color_slug) : "var(--cb-brand)";
    const dot = L.divIcon({
      className: "cb-map-pin",
      html:
        `<span style="display:block;width:16px;height:16px;border-radius:50%;` +
        `background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);"></span>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    const logoUrl = current?.logo_url;
    const logo = logoUrl
      ? L.divIcon({
          className: "cb-map-logo",
          html:
            `<span style="display:block;width:32px;height:32px;border-radius:50%;overflow:hidden;` +
            `border:2px solid ${color};background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.35);">` +
            `<img src="${escapeAttr(logoUrl)}" alt="" ` +
            `style="width:100%;height:100%;object-fit:cover;display:block;" /></span>`,
          iconSize: [36, 36],
          iconAnchor: [18, 18],
        })
      : dot;
    return { dot, logo, hasLogo: Boolean(logoUrl) };
  }

  function applyIcons(zoom: number) {
    if (!config.org) return;
    const { dot, logo, hasLogo } = makeIcons();
    const useLogo = hasLogo && zoom >= LOGO_ZOOM;
    for (const m of markersRef.current) m.setIcon(useLogo ? logo : dot);
  }

  // (Re-)populate markers whenever the data or the map changes.
  useEffect(() => {
    const L = LRef.current;
    const map = mapRef.current;
    const cluster = clusterRef.current;
    if (!L || !map || !cluster) return;

    cluster.clearLayers();
    markersRef.current = [];
    const berlinWide = !config.org;
    const { dot, logo, hasLogo } = berlinWide ? { dot: null, logo: null, hasLogo: false } : makeIcons();
    const useLogo = !berlinWide && hasLogo && map.getZoom() >= LOGO_ZOOM;
    const latLngs: any[] = [];

    for (const a of actions) {
      if (a.lat == null || a.lng == null) continue;
      const lat = Number.parseFloat(a.lat);
      const lng = Number.parseFloat(a.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

      const co = a.co_organizers ?? [];
      const icon = berlinWide
        ? makeDot(L, resolveColor(a.org?.color_slug ?? ""), co.length > 0)
        : (useLogo ? logo : dot);
      const marker = L.marker([lat, lng], { icon, title: a.title });
      const label = a.title || a.meeting_point;
      // Org line only when multi-org (Berlin-wide) — redundant in org-scoped mode.
      const orgLine = berlinWide && a.org
        ? `<br><span style="color:${resolveColor(a.org.color_slug)}">${escapeHtml(a.org.name)}</span>`
        : "";
      // Co-organizer line in both modes (the org-scoped header names only the host).
      const coLine = co.length > 0
        ? `<br><span style="color:${co.length === 1 ? resolveColor(co[0].color_slug) : "var(--cb-muted,#666)"}">${
            co.length === 1 ? escapeHtml(co[0].name) : `${co.length} weitere`
          }</span>`
        : "";
      marker.bindTooltip(
        `<b>${escapeHtml(label)}</b>${orgLine}${coLine}<br>${berlinShortDate(a.start_at)} · ${escapeHtml(a.meeting_point)}`,
        { direction: "top", offset: [0, -10] },
      );
      marker.on("click", () => setSelectedId(a.id));
      cluster.addLayer(marker);
      markersRef.current.push(marker);
      latLngs.push(L.latLng(lat, lng));
    }

    requestAnimationFrame(() => {
      map.invalidateSize();
      if (latLngs.length === 0) map.setView(BERLIN_CENTER, BERLIN_ZOOM);
      else if (latLngs.length === 1) map.setView(latLngs[0], 14);
      else map.fitBounds(L.latLngBounds(latLngs), { padding: [40, 40], maxZoom: 15 });
    });
  }, [actions, status, org]);

  const ungeocoded = actions.filter((a) => a.lat == null || a.lng == null).length;

  return (
    <div class="cb-map-section">
      <div class="cb-map-wrap">
        <div class="cb-map" ref={containerRef} role="region" aria-label="Karte der Cleanup-Aktionen" />
        {status === "loading" && <div class="cb-map-overlay">Karte wird geladen…</div>}
        {status === "error" && (
          <div class="cb-map-overlay cb-error">Karte konnte nicht geladen werden.</div>
        )}
      </div>

      {status === "ready" && ungeocoded > 0 && (
        <p class="cb-map-note">
          {ungeocoded} {ungeocoded === 1 ? "Aktion ist" : "Aktionen sind"} ohne Koordinaten und
          {" "}nicht auf der Karte.
        </p>
      )}

      {selectedId && (
        <Lightbox
          id={selectedId}
          navIds={markerIds}
          onNavigate={setSelectedId}
          config={config}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
};

function makeDot(L: any, color: string, coOrganized = false): any {
  // A co-organized action gets a second outer ring in the host color (#99).
  const ring = coOrganized ? `,0 0 0 3px #fff,0 0 0 5px ${color}` : "";
  const size = coOrganized ? 24 : 20;
  return L.divIcon({
    className: "cb-map-pin",
    html:
      `<span style="display:block;width:16px;height:16px;border-radius:50%;` +
      `background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)${ring};"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string),
  );
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, "&quot;");
}
